import React, { useState, useEffect, useRef, useMemo } from 'react';
import useTopScrollbar from '../hooks/useTopScrollbar';
import { useConfig } from '../context/ConfigContext';
import { getPhToday, formatInPhTime } from '../utils/phTime';
import {
  parseTimeSlotStartHour,
  getCourtRateBands,
  getPolicies,
  formatHourLabel,
  formatTimeSlotRangeParts,
  hasSlotEnded,
  getCourtRate,
  getCourtLabel,
  splitCourtLabelNote,
  getCourtEnvLabel,
  isAddOnCourt,
  isCourtAvailable,
  getGridRowsForDate,
  getTimeSlotsForCourt,
  getMapLinkUrl,
  getBookingWindowDays,
  getRateBandForSlot,
  spinnerRing,
} from '../utils/bookingUtils';
import {
  planRateSeparators,
  isRateSeparatorShown,
  resolveRateSeparatorDesign,
  formatRateBandHours,
  getRateSeparatorPriceText,
} from '../utils/rateSeparatorUtil';
import { isSlotClosed } from '../utils/bookingAvailabilityUtil';
import { hasActivePromo } from '../utils/promoUtils';
// Config -> Booking Settings: block bookings drawn as one button, and the
// minimum-hours / connecting rules that gate the Reserve button.
import { buildBlockSpans, getSelectionRuleIssue, getBookingSettings, describeBlockRange, bookingRulesApply } from '../utils/bookingRulesUtil';
import OpenPlaySessionsSection from './OpenPlaySessionsSection';
import ConnectionErrorScreen from './ConnectionErrorScreen';
import RateSeparatorRow from './RateSeparatorRow';
import CourtRatesTable from './CourtRatesTable';
import SelectedSlotsSummary from './SelectedSlotsSummary';
import VerticalScrollArea from './VerticalScrollArea';
import { useMediaQuery } from '../hooks/useMediaQuery';
import useFloatingPanelSpace from '../hooks/useFloatingPanelSpace';
import usePhClock from '../hooks/usePhClock';
import useMatchedWidths from '../hooks/useMatchedWidths';
import '../css/TimeSlotSelection.css';
import { formatMoney } from '../utils/currencyUtil';
import { UI_DESIGN_VERSIONS, BOOKING_WINDOW_LOCKED_HINT, TIME_SLOT_SUMMARY_STACKED_QUERY } from '../constants/bookingConst';

const WINDOW = 14; // days shown per chip-strip page
// When a block booking can't be booked, the hour that says why is the one
// its button shows — a booking outranks a closure, which outranks the clock.
const BLOCK_STATUS_PRIORITY = ['booked', 'pending', 'unavailable', 'passed', 'openplay'];
const DOW = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const DOW_S = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
const MON = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const MON_S = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/**
 * "Sat, Sep 12" — the date line under an after-midnight row.
 *
 * Parsed field by field, not with `new Date(dateStr)`, which reads a bare
 * date as UTC midnight and lands on the previous day west of Greenwich —
 * the one bug that would make this label say the wrong thing every time.
 *
 * @param {string} dateStr - 'yyyy-MM-dd'
 * @returns {string}
 */
const formatNextDayLabel = (dateStr) => {
  const [y, m, d] = String(dateStr || '').split('-').map(Number);
  if (!y || !m || !d) return '';
  const dt = new Date(y, m - 1, d);
  return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};
const sameYMD = (a, b) => !!a && !!b && dateKey(a) === dateKey(b);
const diffDays = (a, b) => Math.round((a.getTime() - b.getTime()) / 86400000);

// Guests can now accumulate selections across MULTIPLE dates instead of
// just one (see the .tss-summary column below) — `selectedDates` is
// `{ [dateStr]: { [timeSlot]: court[] } }`, keyed by every date the guest
// has picked hours on so far, not just whichever date the grid currently
// happens to be showing. Switching the viewed date (via the chip strip or
// jump-to-date) no longer clears anything; it only changes which date's
// grid is rendered. `onCourtToggle` now takes the date it applies to as its
// first argument, and doubles as "remove" when called on an already-
// selected (date, timeSlot, court) triple — used both by the grid cells and
// by each summary row's own remove (x) button.
/**
 * @param {boolean} [isFetchingSlots] - true while the selected date's
 *   bookings are still loading. Covers the slots column with a spinner
 *   rather than blanking the page, so the date strip above stays visible and
 *   tappable and the guest keeps their bearings on every date change.
 * @param {boolean} [fetchTimedOut] - that fetch ran past the configured
 *   timeout (see hooks/useLoadingTimeout.js); the same connection screen the
 *   rest of the app uses takes the spinner's place.
 * @param {() => void} [onRetryFetch] - Try Again; re-subscribes to the date.
 * @param {string} [design] - UI_DESIGN_VERSIONS value (Config -> Theme -> UI
 *   Design). Only Version 2's presentation differs — see TimeSlotSelectionV2,
 *   which is how that version is rendered; Version 1 is the default.
 */
function TimeSlotSelection({
  selectedDate, selectedDates, sportId = null, onDateSelect, onCourtToggle, onClearAll, bookedSlots,
  slotOverrides = {}, onJoinOpenPlay, onOpenPlaySessionClick, onContinue, canContinue,
  isFetchingSlots = false, fetchTimedOut = false, onRetryFetch,
  // The NEXT date's bookings and overrides, for the after-midnight rows a
  // club trading past midnight shows under the selected day (Config ->
  // Pricing -> Open Past Midnight Until). Empty when the club ends at
  // midnight, which is when no such row is rendered anyway.
  nextDayBookedSlots = {}, nextDaySlotOverrides = {},
  design = UI_DESIGN_VERSIONS.V1,
}) {
  const { config } = useConfig();
  const isV2 = design === UI_DESIGN_VERSIONS.V2;
  // The sport this booking screen is scoped to (Config -> Sports & Courts)
  // — falls back to the first configured sport so a single-sport club (no
  // sportId ever passed) behaves exactly as before.
  const resolvedSport = (config?.sports || []).find(s => s.id === sportId) || config?.sports?.[0];
  const today = useMemo(() => getPhToday(), []);
  // Live PH clock, so an hour turns Passed the minute it ends while the page
  // is open rather than only on the next load.
  const phClock = usePhClock();

  const [winStart, setWinStart] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);
  const jumpInputRef = useRef(null);
  // Version 2 only: the slot grid, so its fade-in can be replayed (below).
  const slotsGridRef = useRef(null);

  // Config -> System -> Advance booking window. `bookableDays` counts today
  // itself, so a 30-day window is today plus the next 30 dates; `lastDay` is
  // the furthest date a guest may pick. Everything below — how many chips
  // exist, when the next arrow dies, what the date picker accepts — is
  // derived from these two rather than each re-deriving the limit.
  const windowDays = getBookingWindowDays(config);
  const bookableDays = windowDays + 1;
  const lastDay = useMemo(() => addDays(today, windowDays), [today, windowDays]);

  // Keep the visible 14-day window in sync with whatever date is selected.
  // Clamped to the last page of the booking window: a selected date beyond
  // it (an Open Play session hosted further out, a date restored from an
  // earlier visit) would otherwise page the strip somewhere with no chips
  // on it at all.
  useEffect(() => {
    if (!selectedDate) return;
    const diff = Math.min(diffDays(selectedDate, today), windowDays);
    setWinStart(Math.max(0, Math.floor(diff / WINDOW) * WINDOW));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate ? dateKey(selectedDate) : null, windowDays]);

  // Courts belonging to the active sport (Config -> Sports & Courts).
  // Falls back to the old flat numberOfCourts count when config.courts
  // hasn't been populated yet (a config saved before that migration ran),
  // so this never renders an empty grid for an existing deployment.
  const sportCourts = (config?.courts || []).filter(c => !sportId || c.sportId === sportId);
  const courts = sportCourts.length > 0
    ? sportCourts.map(c => Number(c.number)).sort((a, b) => a - b)
    : Array.from({ length: config?.numberOfCourts || 0 }, (_, i) => i + 1);
  // Plain chronological order: the grid opens on the first slot of the day
  // (12:00 - 1:00 MN when the config includes it) and runs straight through
  // to the last. It briefly started at 6AM instead, wrapping the overnight
  // hours to the bottom; that made the first row of the grid disagree with
  // every other list of slots in the app, so it is back to midnight-first.
  // orderSlotsFromDayStart / GUEST_DAY_START_HOUR are left in place for
  // whenever a club wants the shifted view back.
  // Resolved PER DATE, not once for the club: opening hours can differ by
  // weekday (Config -> Sports & Courts -> Time Slots), so a Saturday grid may
  // legitimately be shorter than a Tuesday one. An empty result is a real
  // answer — the club is closed that day — and renders as such below rather
  // than as an empty grid.
  //
  // Scoped to THIS sport's courts, and the union across them: a set can be
  // scoped to particular courts (Config -> Sports & Courts -> Time Slots), so
  // an hour only one court is open for still needs a row or it could never be
  // booked. The courts missing that hour render it as CLOSED below.
  // The rows the grid actually draws: this date's hours, then the early hours
  // of the NEXT date for a club that trades past midnight. Each row carries
  // the date it belongs to — a 1-2AM row under Friday is SATURDAY's cell, and
  // its booking, override, rate and slot lock all resolve against Saturday.
  const gridRows = useMemo(
    () => getGridRowsForDate(config, selectedDate ? dateKey(selectedDate) : null, courts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.timeSlots, config?.timeSlotSets, config?.courts, config?.nextDayGridEndHour, selectedDate, courts.join(',')],
  );

  // Per-court hours for the next date, so an after-midnight row can be CLOSED
  // on a court the way any other row can — the court scoping that applies to
  // 2PM applies to 2AM.
  const nextDayCourtSlots = useMemo(() => {
    const nextRow = gridRows.find(r => r.dayOffset === 1);
    if (!nextRow) return {};
    return Object.fromEntries(courts.map(court => [
      court,
      new Set(getTimeSlotsForCourt(config, nextRow.dateStr, court)),
    ]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.timeSlots, config?.timeSlotSets, gridRows, courts.join(',')]);

  /**
   * Whether a row's hour is completely over — Passed only once its END time
   * has gone by (a 4-5PM row from 5:01 PM), so a guest can still book an hour
   * that has started. Measured against the ROW's own date, not the viewed
   * one: viewing today, a 1AM row is tomorrow and cannot have passed.
   * @param {{slot: string, dateStr: string}} row
   * @returns {boolean}
   */
  const isRowPassed = (row) => {
    if (!row?.dateStr) return false;
    return hasSlotEnded(row.dateStr, row.slot, phClock.todayStr, phClock.minutes);
  };

  // Every cell a promo covers, as "date|hour|court" -> the promo price. Built once
  // per render rather than asked per cell: the grid draws ~18 hours against
  // every court, and each question walks the promo list.
  //
  // The price comes from getCourtRate, not from the promo rule directly — that
  // is the same function the checkout and the receipt use, so the number on
  // the cell cannot drift from the one the guest is charged.
  const promoCells = useMemo(() => {
    const priced = new Map();
    const sportId = resolvedSport?.id || null;
    // Keyed by the ROW's date: an after-midnight row belongs to the next day,
    // and a promo that runs tonight need not run tomorrow.
    gridRows.forEach(({ slot: timeSlot, dateStr }) => {
      courts.forEach((court) => {
        if (hasActivePromo(config, { court, timeSlot, sportId, dateStr })) {
          priced.set(`${dateStr}|${timeSlot}|${court}`, getCourtRate(config, court, timeSlot, sportId, dateStr));
        }
      });
    });
    return priced;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.promos, config?.courts, selectedDate, resolvedSport?.id, courts.join(','), gridRows]);

  // { [court]: Set(hours it is open) } — resolved once per render rather than
  // per cell, since the grid draws every hour against every court.
  const courtSlots = useMemo(() => {
    const dateStr = selectedDate ? dateKey(selectedDate) : null;
    return Object.fromEntries(courts.map(court => [
      court,
      new Set(getTimeSlotsForCourt(config, dateStr, court)),
    ]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.timeSlots, config?.timeSlotSets, selectedDate, courts.join(',')]);
  // Drives the scrollbar rail rendered above the grid (see the hook for why
  // this isn't the CSS rotateX trick any more). Declared after `courts` and
  // `gridRows` — it reads both.
  const { trackRef, thumbRef, scrollerRef } = useTopScrollbar([courts.length, gridRows.length]);

  // Version 2 fades the grid in each time a date's slots land. Replayed by
  // toggling the class rather than re-keying the grid: useTopScrollbar watches
  // this exact element with a ResizeObserver, and a remounted grid would leave
  // it observing a detached node. Waits for the fetch so the fade plays where
  // the guest can see it, not under the loading cover.
  const fadeDateKey = selectedDate ? dateKey(selectedDate) : null;
  useEffect(() => {
    const grid = slotsGridRef.current;
    if (!isV2 || isFetchingSlots || !grid) return;
    grid.classList.remove('tss-fade-in');
    // Reading offsetWidth forces a reflow, which is what lets the same
    // animation class start over when it is added straight back.
    void grid.offsetWidth;
    grid.classList.add('tss-fade-in');
  }, [isV2, isFetchingSlots, fadeDateKey]);
  // { [court]: boolean } — resolved once per render rather than calling
  // isCourtAvailable for every court on every one of the ~17 slot rows.
  const courtAvailability = useMemo(
    () => Object.fromEntries(courts.map(c => [c, isCourtAvailable(config, c)])),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config, courts.join(',')]
  );
  // Where each block booking sits on this grid: the first row of each block
  // per court, and the rows it swallows (see buildBlockSpans).
  const blockSpans = useMemo(
    () => buildBlockSpans(config, gridRows, courts),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.bookingSettings, config?.timeSlots, config?.timeSlotSets, gridRows, courts.join(',')],
  );
  // A separator row wherever the hourly rate changes (Config -> Sports &
  // Courts -> Hourly rates), and the grid line each hour row lands on once
  // those rows are slotted in. Bands resolve against each ROW's own date, so
  // an after-midnight row follows the next day's weekday-scoped rates.
  // Config -> Sports & Courts -> Hourly rates can switch them off; a band-less
  // lookup then plans no separators, so every row keeps its plain grid line.
  const showSeparators = isRateSeparatorShown(config);
  const separatorDesign = resolveRateSeparatorDesign(config);
  const rateSeparators = useMemo(
    () => planRateSeparators(
      gridRows,
      showSeparators
        ? row => getRateBandForSlot(config, row.slot, resolvedSport?.id || null, row.dateStr)
        : () => null,
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.sports, config?.priceOffPeak, config?.pricePeak, config?.peakRateStart, resolvedSport?.id, gridRows, showSeparators],
  );
  const rowLines = rateSeparators.rowLines;
  // Every separator pill as wide as the widest one, so the rate labels line
  // up down the grid; and the time column's width, so each pill centres over
  // the court buttons only (RateSeparatorRow.css reads both).
  useMatchedWidths(slotsGridRef, '.rsr-row .rsp-pill', '--rsr-pill-w', [rateSeparators, config, courts.join(',')]);
  useMatchedWidths(slotsGridRef, '.tss-time-cell', '--rsr-lead', [rateSeparators, gridRows, courts.join(',')]);

  // The first booking rule the current picks break, across every selected
  // date — null when Reserve can go ahead. Recomputed per selection change
  // only; the rule check walks the selection, not the grid.
  const ruleIssue = useMemo(() => getSelectionRuleIssue(config, selectedDates), [config, selectedDates]);
  // Shown above the grid only when the minimum actually covers the date in
  // view and at least one of this sport's courts (Config -> Booking Settings
  // -> Applies to).
  const bookingSettings = getBookingSettings(config);
  const ruleDateKey = selectedDate ? dateKey(selectedDate) : null;
  const minBookingHours = ruleDateKey && courts.some(court => bookingRulesApply(bookingSettings, ruleDateKey, court))
    ? bookingSettings.minBookingHours
    : 1;

  // Excludes items the club owns none of (inventoryTotal 0), same as the
  // rental modal — no point advertising an add-on in Court Information
  // that can never actually be rented.
  const sportEquipment = (config?.equipment || []).filter(item => (
    (!sportId || item.sportId === sportId) && (Number(item.inventoryTotal) || 0) > 0
  ));

  const isViewingToday = sameYMD(selectedDate, today);
  const viewedDateKey = selectedDate ? dateKey(selectedDate) : null;
  // Only the currently-VIEWED date's selections show as "Selected" in the
  // grid below — the other accumulated dates still count toward the
  // summary/total, they just aren't the grid being looked at right now.

  /**
   * What a taken hour reads on the public grid.
   *
   * Plain "Booked" by default. A court with "Booked label" ticked (Config ->
   * Sports & Courts, beside Available) appends the booker's INITIALS —
   * "Booked (BN)" — never a readable name, since this grid is public: it
   * should confirm an hour is taken without saying who took it. First + last
   * initial, matching the admin grid's own badges; a single-word name falls
   * back to its first two letters ("Bryan" -> "BR") so it's never a lone
   * character. The cell CSS uppercases the result.
   *
   * @param {object|null} booking
   * @param {number|string} court
   * @returns {string} '' when the hour isn't booked
   */
  const getDisplayName = (booking, court) => {
    if (!booking) return '';
    if (booking.status !== 'confirmed') return 'Pending';
    // An hour the club took off the market has no booker to name, and
    // "Booked (AD)" would read as a guest with initials. Checked before the
    // court's own setting so ticking "Booked label" can never expose it.
    if (booking.adminBlock) return 'Booked';

    const courtRecord = (config?.courts || []).find(c => Number(c.number) === Number(court));
    if (courtRecord?.showBookedInitials !== true) return 'Booked';

    const parts = (booking.userName || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return 'Booked';
    const first = parts[0][0] || '';
    const last = parts.length > 1 ? parts[parts.length - 1][0] : (parts[0][1] || '');
    const initials = `${first}${last}`.toUpperCase().trim();
    return initials ? `Booked (${initials})` : 'Booked';
  };

  // Flattens every accumulated (date, timeSlot, court) selection — across
  // ALL picked dates, not just the one currently viewed — into one row per
  // court-hour, chronologically sorted. Each row maps 1:1 to a single
  // onCourtToggle call, so a summary row's remove (x) button can deselect
  // exactly that one court-hour without touching anything else.
  const summaryRows = useMemo(() => {
    const rows = [];
    Object.keys(selectedDates).sort().forEach(dateStr => {
      const slots = selectedDates[dateStr] || {};
      Object.keys(slots)
        .sort((a, b) => parseTimeSlotStartHour(a) - parseTimeSlotStartHour(b))
        .forEach(timeSlot => {
          (slots[timeSlot] || []).slice().sort((a, b) => a - b).forEach(court => {
            // dateStr matters: rate bands can be scoped to specific weekdays,
            // so the same slot can cost different amounts across the dates in
            // one multi-date selection.
            rows.push({ dateStr, timeSlot, court, price: getCourtRate(config, court, timeSlot, sportId, dateStr) });
          });
        });
    });
    return rows;
  }, [selectedDates, config, sportId]);

  const distinctDateCount = Object.keys(selectedDates).filter(d => Object.keys(selectedDates[d] || {}).length > 0).length;
  const totalSelectedCourts = summaryRows.length;
  const totalPrice = summaryRows.reduce((sum, row) => sum + row.price, 0);

  // Small screens put the summary under the grid, out of sight while the
  // guest picks hours — so once anything is picked it floats over the bottom
  // of the screen instead, with Reserve and Clear on one row. The page gets
  // matching room at the bottom so nothing ends up hidden behind it.
  //
  // Its × sends it back to its place under the grid. Picking another hour
  // floats it again: the guest is choosing hours again, and that's when the
  // running total is worth having in view.
  const stackedLayout = useMediaQuery(TIME_SLOT_SUMMARY_STACKED_QUERY);
  const [summaryDocked, setSummaryDocked] = useState(false);
  const previousSelectedCount = useRef(totalSelectedCourts);
  useEffect(() => {
    if (totalSelectedCourts > previousSelectedCount.current || totalSelectedCourts === 0) setSummaryDocked(false);
    previousSelectedCount.current = totalSelectedCourts;
  }, [totalSelectedCourts]);
  const floatingSummary = stackedLayout && totalSelectedCourts > 0 && !summaryDocked;
  const summaryRef = useFloatingPanelSpace(floatingSummary);

  // summaryRows, bucketed under one entry per date — so the date badge
  // renders once per date (as a group header) instead of once per row.
  const summaryGroups = useMemo(() => {
    const groups = [];
    const byDate = new Map();
    summaryRows.forEach(row => {
      if (!byDate.has(row.dateStr)) {
        const group = { dateStr: row.dateStr, rows: [] };
        byDate.set(row.dateStr, group);
        groups.push(group);
      }
      byDate.get(row.dateStr).rows.push(row);
    });
    return groups;
  }, [summaryRows]);

  // "Sun, Aug 9" — short weekday + month + day, for the summary's per-row
  // date badge (only shown once 2+ dates are selected — see the render
  // below). Parsed manually from the "yyyy-MM-dd" key instead of `new
  // Date(dateStr)` to avoid that string form being read as UTC midnight.
  const formatDateBadge = (dateStr) => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const dt = new Date(y, m - 1, d);
    return `${DOW_S[dt.getDay()]}, ${MON_S[dt.getMonth()]} ${dt.getDate()}`;
  };

  const handleJumpChange = (e) => {
    if (!e.target.value) return;
    const [y, m, d] = e.target.value.split('-').map(Number);
    const picked = new Date(y, m - 1, d);
    // The input's `min`/`max` grey out-of-range dates in the picker UI, but
    // a typed-in date bypasses that in most browsers — re-check here so
    // neither a past date nor one beyond the booking window can actually be
    // selected.
    if (dateKey(picked) < dateKey(today)) return;
    if (dateKey(picked) > dateKey(lastDay)) return;
    onDateSelect(picked);
  };
  // The date input itself is the tap target — a transparent overlay sitting
  // on top of the styled button (see .tss-jumpdate). It has to work that way
  // for iOS: Safari will not open a date picker from a programmatic
  // showPicker()/click() on an input the user didn't touch directly, so the
  // old "invisible input + button that calls showPicker()" version did
  // nothing at all on iPhone while working fine on Android and desktop.
  //
  // showPicker() is still called on desktop, where clicking the text part of
  // a date field only focuses a segment rather than opening the calendar.
  // It throws on browsers that reject the call; iOS has already opened its
  // own picker from the tap by then, so there's nothing to do in the catch.
  const openJumpPicker = () => {
    const el = jumpInputRef.current;
    if (!el || typeof el.showPicker !== 'function') return;
    try {
      el.showPicker();
    } catch {
      // Intentionally ignored — see above.
    }
  };

  // ---- Rates info bar (per-court day/night rate, sourced from config) ----
  // Prefers a court's own dayRate/nightRate override (Config -> Sports &
  // Courts), falling back to the active sport's rates, then the legacy
  // flat courtRates/priceOffPeak/pricePeak fields for a pre-migration config.
  // Every rate band that applies to a court, with that court's effective
  // price for each — straight from the shared resolver, so this strip can't
  // drift from what checkout actually charges (it used to re-derive its own
  // day/night pair here and had to be kept in sync by hand).
  // Scoped to the date being viewed, so a club with weekend-only rates shows
  // the guest the prices that actually apply to the day in front of them
  // rather than every band it has ever configured.
  const courtBands = (court) => getCourtRateBands(config, court, resolvedSport?.id || null, viewedDateKey);
  // The day's opening and closing hour, read off `gridRows` — the SAME list
  // the grid below is drawn from (getGridRowsForDate). These are labels only;
  // nothing here decides which rows exist or which of them can be booked.
  //
  // They used to come from getTimeSlotsForDate, which is the date's raw slot
  // list in plain 0-23 order, and the two answer different questions. For a
  // club open 4PM-2AM that list sorts 12-1AM and 1-2AM to the FRONT (they are
  // hour 0 and 1 of this date) and 11-12PM to the back, so the strip announced
  // "12:00 MN - 12:00 MN" while the grid plainly showed 4:00 PM through
  // 2:00 AM. The grid is right: those small hours are drawn under the previous
  // night, and the day runs on into the next morning as far as Config ->
  // Pricing -> "Open Past Midnight Until". Reading the first and last row it
  // actually draws is the only way these can't drift apart again.
  const openHour = gridRows.length ? parseTimeSlotStartHour(gridRows[0].slot) : 6;
  const closeHour = gridRows.length ? (parseTimeSlotStartHour(gridRows[gridRows.length - 1].slot) + 1) % 24 : 22;
  const hoursHint = `Open ${formatHourLabel(openHour)} - ${formatHourLabel(closeHour)} daily`;

  /**
   * The hours to print against a rate band, in the strip above the grid, the
   * Court Information panel and the rate separators. A whole-day band borrows
   * the hours the grid draws (see rateSeparatorUtil.formatRateBandHours).
   *
   * @param {{startHour: number, endHour: number}} band
   * @param {string} [sep] - what goes between the hours; the compact Court
   *   Information panel passes an unspaced en dash to match its own type
   * @returns {string} e.g. "4:00 PM - 2:00 AM"
   */
  const formatBandHours = (band, sep = ' - ') => formatRateBandHours(band, gridRows, sep);

  // Courts collapsed into one entry per DISTINCT day/night rate pair, so the
  // Court Information panel prints "Courts 1, 4, 5, 6: ₱200/hr ..." once
  // instead of six identical lines — most clubs price every court the same,
  // which made that list pure noise. Insertion order follows `courts`, so
  // groups still read lowest-court-number first.
  const rateGroups = (() => {
    const byRate = new Map();
    courts.forEach(court => {
      const bands = courtBands(court);
      // Keyed on the court's full price list so courts only share a line
      // when they genuinely cost the same in every band.
      const key = bands.map(b => `${b.band.id}:${b.price}`).join('|');
      if (!byRate.has(key)) byRate.set(key, { key, bands, courts: [] });
      byRate.get(key).courts.push(court);
    });
    return [...byRate.values()];
  })();

  // The bands themselves, for the rows above the grid. Every group carries
  // the same band list (they all come from the active sport) — only the
  // prices differ per court group.
  const displayBands = rateGroups[0]?.bands.map(entry => entry.band) || [];

  // "All courts" only when every court genuinely shares one rate — with two
  // or more groups each has to name its own courts to stay unambiguous.
  //
  // Repeating the word "Court" per entry ("Court 1, Court 4, Court 5,
  // Court 6") overflowed the compact rates strip above the grid, so numbers
  // get pulled out of the label and consecutive runs collapse into a range:
  // "Courts 1, 4–6". That's only safe for courts still using their default
  // "Court N" label — once an admin names one (Config -> Sports & Courts)
  // its name has to be printed verbatim, so those fall back to a plain
  // comma-joined list.
  const rateGroupLabel = (group) => {
    if (rateGroups.length === 1 && courts.length > 1) return 'All courts';

    const allDefaultNames = group.courts.every(c => getCourtLabel(config, c) === `Court ${c}`);
    if (!allDefaultNames) return group.courts.map(c => getCourtLabel(config, c)).join(', ');

    const nums = [...group.courts].sort((a, b) => a - b);
    const parts = [];
    let runStart = nums[0];
    let prev = nums[0];
    const flush = () => {
      if (runStart === prev) parts.push(`${runStart}`);
      else if (prev - runStart === 1) parts.push(`${runStart}, ${prev}`);
      else parts.push(`${runStart}–${prev}`); // en dash, e.g. "4–6"
    };
    nums.slice(1).forEach(n => {
      if (n === prev + 1) { prev = n; return; }
      flush();
      runStart = n;
      prev = n;
    });
    flush();
    return `${nums.length > 1 ? 'Courts' : 'Court'} ${parts.join(', ')}`;
  };

  // Version 2's Court Rates table: the same bands and prices as Version 1's
  // rates strip, laid out one row per band with one column per court group.
  const rateTableRows = displayBands.map(band => ({
    id: band.id,
    name: band.name || '',
    hours: formatBandHours(band, ' \u2013 '),
    prices: rateGroups.map(group => ({
      key: group.key,
      label: rateGroupLabel(group),
      value: `${formatMoney(group.bands.find(b => b.band.id === band.id)?.price ?? 0, config)}/hr`,
    })),
  }));

  // ---- Date-strip chips ----
  // The final page is short whenever the booking window doesn't divide
  // evenly into WINDOW-day pages, so the chip count (and the month label's
  // end date) come off what's actually bookable, not off a fixed 14.
  // Version 2 always draws a full page of chips and greys out the dates past
  // the window instead (see the chip render), so its page never comes up short.
  const winChipCount = isV2 ? WINDOW : Math.max(0, Math.min(WINDOW, bookableDays - winStart));
  const isLastWindow = winStart + WINDOW >= bookableDays;
  const winFirst = addDays(today, winStart);
  const winLast = addDays(today, winStart + Math.max(0, winChipCount - 1));
  const winLabel = winFirst.getMonth() === winLast.getMonth()
    ? `${MON[winFirst.getMonth()]} ${winFirst.getFullYear()}`
    : `${MON_S[winFirst.getMonth()]} – ${MON_S[winLast.getMonth()]} ${winLast.getFullYear()}`;

  // Cards with nothing typed in them are skipped rather than rendering an
  // empty bullet — an admin mid-edit shouldn't leak a blank row to guests.
  const courtDetails = (config?.courtDetails || []).filter(d => (d.title || '').trim() || (d.description || '').trim());

  const faqItems = [
    {
      title: '1. Court hours',
      content: (
        <>
          {/* Hours now come from the configured time slots rather than a
              free-text field — see the Operating Hours card in Config ->
              Court Details for anything more specific. */}
          <p>We are open {formatHourLabel(openHour)} - {formatHourLabel(closeHour)}, daily.</p>
          {rateGroups.map(group => (
            <p key={group.key}>
              <strong>{rateGroupLabel(group)}</strong>:{' '}
              {group.bands.map(({ band, price }, i) => (
                <React.Fragment key={band.id}>
                  {i > 0 && ' \u00b7 '}
                  {band.name ? `${band.name} ` : ''}{formatMoney(price, config)}/hr
                  {' ('}{formatBandHours(band, '–')}{')'}
                </React.Fragment>
              ))}
            </p>
          ))}
          {sportEquipment.length > 0 && (
            <>
              <p><strong>Add-ons:</strong></p>
              <ul>
                {sportEquipment.map(item => (
                  <li key={item.id}>{item.label} rental ({formatMoney(item.price, config)} / {item.unit})</li>
                ))}
              </ul>
            </>
          )}
        </>
      ),
    },
    {
      title: '2. Location',
      content: (
        <p>
          {config?.locationText ? `We are located at: ${config.locationText}.` : 'Location details coming soon.'}
          {/* getMapLinkUrl, not config.mapsUrl — an embed URL opened as a
              page errors with "must be used in an iframe". */}
          {getMapLinkUrl(config) ? (
            <> {' '}<a href={getMapLinkUrl(config)} target="_blank" rel="noopener noreferrer">View on Google Maps</a>.</>
          ) : null}
        </p>
      ),
    },
    {
      title: '3. Court Details',
      // Free-form cards from Config -> Court Details, in the order the
      // admin arranged them. Replaces the old fixed surface/lighting/
      // amenities/what's-included fields.
      content: (
        <ul>
          {courtDetails.map(detail => (
            <li key={detail.id}>
              {/* .admin-text (global.css) keeps the line breaks the admin
                  typed into the Description textarea — without it HTML runs
                  every line together into one paragraph. */}
              {detail.title ? <strong>{detail.title}: </strong> : null}
              <span className="admin-text">{detail.description}</span>
            </li>
          ))}
        </ul>
      ),
    },
    {
      title: '4. Reminders/Policies',
      content: (
        <>
          {/* Admin-managed cards (Config -> Policies) rather than five fixed
              rules, rendered in the order the admin arranged them.
              getPolicies falls back to the old rule fields for a config
              saved before the change. */}
          {getPolicies(config).map(policy => (
            <p key={policy.id}>
              {policy.title && <><strong>{policy.title}</strong><br /></>}
              {/* .admin-text (global.css) — a policy typed as three lines in
                  Config renders as three lines here, instead of HTML
                  collapsing the newlines into spaces. */}
              <span className="admin-text">{policy.description}</span>
            </p>
          ))}
        </>
      ),
    },
  ];

  /**
   * Everything one grid cell needs to render: its status, label, whether a
   * guest can tap it, and the promo it carries. Pulled out of the render so a
   * block booking can resolve each of its hours the same way and then draw
   * them as one button (see renderBlockCell).
   *
   * @param {{slot: string, dateStr: string, dayOffset: number}} row
   * @param {number} court
   * @param {{ignorePassed?: boolean}} [options] - ignorePassed: treat the hour as
   *   not passed (a block booking's earlier hours while the block is still running)
   * @returns {{status: string, label: string, clickable: boolean, isOpenPlay: boolean,
   *   isSelected: boolean, onPromo: boolean, promoPrice: number|null, title: string}}
   */
  const resolveCellState = (row, court, { ignorePassed = false } = {}) => {
    const timeSlot = row.slot;
    // EVERY read below uses the row's own date. For an ordinary row that is
    // the viewed date; for an after-midnight row it is the next one.
    const rowDate = row.dateStr;
    const isNextDay = row.dayOffset === 1;
    const rowBookedSlots = isNextDay ? nextDayBookedSlots : bookedSlots;
    const rowOverrides = isNextDay ? nextDaySlotOverrides : slotOverrides;
    const rowCourtSlots = isNextDay ? nextDayCourtSlots : courtSlots;
    const selectedCourts = (selectedDates[rowDate] || {})[timeSlot] || [];
    const passed = !ignorePassed && isRowPassed(row);

    const booking = (rowBookedSlots[timeSlot] || []).find(b => b.court === court) || null;
    const isBooked = !!booking;
    const isSelected = selectedCourts.includes(court);
    // Admin opened this specific court/hour up for Open
    // Play instead of a normal reservation (Admin ->
    // Manage availability grid's "Open Play" status —
    // see AdminTimeSlotSelection.jsx). Takes over the
    // whole cell: no court toggle, just a join button.
    const isOpenPlay = !isBooked && rowOverrides?.[timeSlot]?.[court] === 'openPlay';
    // Admin has closed this whole court (Config ->
    // Sports & Courts -> Available) — outranks every
    // other state except an existing booking, which
    // still needs to show so a guest who already
    // reserved it isn't confused about where it went.
    const courtClosed = !courtAvailability[court];
    // Admin closed THIS hour on THIS court from the
    // Manage availability grid ("Closed" status). Same
    // effect as courtClosed, just scoped to one cell
    // instead of the whole column — an hour out for
    // maintenance or a private event. An existing
    // booking still wins, for the same reason a closed
    // court doesn't hide one: a guest who already
    // reserved it shouldn't watch it vanish.
    const slotClosed = isSlotClosed(rowOverrides, timeSlot, court);
    // This hour exists on the grid because ANOTHER court
    // is open for it, but a time-slot set scoped to
    // courts leaves this one shut. Same closed treatment
    // as an admin closing the cell by hand — from the
    // guest's side the two are the same fact.
    const outOfHours = !rowCourtSlots[court]?.has(timeSlot);
    // An add-on column (Config -> Sports & Courts ->
    // Court type "Add-on") isn't a court to reserve —
    // it's an extra attached to a court hour. "Open"
    // invites booking it on its own, which is exactly
    // what it can't do, so its free hours read "Add".
    const isAddOn = isAddOnCourt(config, court);

    let status = 'open';
    let label = isAddOn ? 'Add' : 'Open';
    if (isBooked) {
      status = booking.status === 'confirmed' ? 'booked' : 'pending';
      label = booking.status === 'confirmed' ? 'Booked' : 'Pending';
    } else if (courtClosed) {
      status = 'unavailable';
      label = 'Unavailable';
    } else if (slotClosed || outOfHours) {
      // Same blocked treatment and the same hatched
      // 'unavailable' styling as a switched-off court,
      // but worded differently on purpose: "Unavailable"
      // is the whole court being out of service, while
      // this is one hour the club has closed. The two
      // read alike at a glance and still tell a guest
      // which of the two they are looking at.
      // (.tss-slot-status uppercases it — renders CLOSED.)
      status = 'unavailable';
      label = 'Closed';
    } else if (passed) {
      status = 'passed';
      label = 'Passed';
    } else if (isOpenPlay) {
      status = 'openplay';
      label = 'Open Play';
    } else if (isSelected) {
      status = 'selected';
      label = 'Selected';
    }

    const bookingStatusLabel = getDisplayName(booking, court);
    if (bookingStatusLabel !== '') {
      label = bookingStatusLabel;
    }

    const clickable = !isBooked && !passed && !courtClosed && !slotClosed && !outOfHours;
    // Flagged only where the guest can act on it. A
    // promo on an hour that is booked, closed or gone
    // is an offer they cannot take, and badging it
    // would read as availability.
    const onPromo = clickable && promoCells.has(`${rowDate}|${timeSlot}|${court}`);
    const promoPrice = onPromo ? promoCells.get(`${rowDate}|${timeSlot}|${court}`) : null;
    // Deliberately no guest name here either — the
    // tooltip would have leaked what the initials are
    // hiding.
    const title = isBooked
      ? (booking.status === 'confirmed' ? 'Already booked' : 'Pending approval')
      : courtClosed ? 'This court is temporarily unavailable'
        : slotClosed ? 'This hour is closed'
          : passed ? 'This time has passed'
            : isOpenPlay ? 'Tap to join Open Play' : label;

    return { status, label, clickable, isOpenPlay, isSelected, onPromo, promoPrice, title };
  };

  /**
   * One block booking (Config -> Booking Settings) drawn as a single button
   * spanning every hour of it. A tap selects or releases the whole block —
   * App.jsx's handleCourtToggle expands the one cell to all of its hours —
   * so a guest can never hold half of one.
   *
   * Bookable only when EVERY hour is: a block with one hour taken or closed
   * can't be sold whole, and it shows the state of the hour that stops it.
   *
   * @param {{rowSpan: number, slots: string[], dateStr: string}} span - from buildBlockSpans
   * @param {Array<object>} rows - the grid rows the span covers
   * @param {number} court
   * @param {number} rowIndex - first row, for grid placement
   * @param {number} courtIndex - for grid placement
   * @returns {JSX.Element}
   */
  const renderBlockCell = (span, rows, court, rowIndex, courtIndex) => {
    // A block is Passed only once its LAST hour is over (5PM-12MN from
    // 12:01 AM). Until then its earlier hours ending doesn't count against
    // it — the same rule a single hour follows, applied to the block as one.
    const blockEnded = isRowPassed(rows[rows.length - 1]);
    const states = rows.map(r => resolveCellState(r, court, { ignorePassed: !blockEnded }));
    const bookable = states.every(st => st.clickable && !st.isOpenPlay);
    let status;
    let label;
    if (bookable) {
      status = states.some(st => st.isSelected) ? 'selected' : 'open';
      label = status === 'selected' ? 'Selected' : states[0].label;
    } else {
      const blocking = BLOCK_STATUS_PRIORITY
        .map(st => states.find(cell => cell.status === st))
        .find(Boolean) || states.find(cell => !cell.clickable) || states[0];
      status = blocking.status;
      label = blocking.label;
    }
    const hours = span.slots.length;
    return (
      <button
        key={court}
        type="button"
        className={`tss-slot tss-slot-block ${status}`}
        // From its first row's line to just past its last row's, so a rate
        // separator falling inside the block is spanned too.
        style={{ gridRow: `${rowLines[rowIndex]} / ${rowLines[rowIndex + span.rowSpan - 1] + 1}`, gridColumn: courtIndex + 2 }}
        disabled={!bookable}
        onClick={() => { if (bookable) onCourtToggle(span.dateStr, span.slots[0], court); }}
        title={bookable
          ? `${describeBlockRange(span.block)} — booked as one ${hours}-hour block`
          : states.find(cell => cell.status === status)?.title || label}
      >
        <span className="tss-slot-status">{label}</span>
        <span className="tss-slot-block-note">{hours}-hour block</span>
      </button>
    );
  };

  return (
    <div className={`tss-root${isV2 ? ' tss-v2' : ''}`}>
      {isV2 && selectedDate && (
        <CourtRatesTable rows={rateTableRows} columnCount={rateGroups.length} />
      )}
      <div className="tss-card">
        {/* ---- Date bar ---- */}
        <div className="tss-datebar">
          <div className="tss-daterow">
            <button
              type="button"
              className="tss-arrow"
              aria-label="Previous 14 days"
              disabled={winStart === 0}
              onClick={() => setWinStart(w => Math.max(0, w - WINDOW))}
            >&lsaquo;</button>

            <div className="tss-curdate">
              <div className="tss-dow">{selectedDate ? (isViewingToday ? 'Today' : DOW[selectedDate.getDay()]) : '—'}</div>
              <div className="tss-full">{selectedDate ? formatInPhTime(selectedDate, 'EEEE, MMMM d, yyyy') : '—'}</div>
            </div>

            {/* The pill is presentational only; the real control is the
                transparent date input layered over it. Making the input the
                thing you actually tap is what fixes iOS, and it means there's
                one focusable control here rather than a button and an input
                that both claim to be "Jump to date". */}
            <span className="tss-jumpwrap">
              <span className="tss-jumpbtn" aria-hidden="true">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <path d="M16 2v4M8 2v4M3 10h18" />
                </svg>
                <span className="tss-jl">Jump to date</span>
              </span>
              <input
                ref={jumpInputRef}
                type="date"
                className="tss-jumpdate"
                min={dateKey(today)}
                max={dateKey(lastDay)}
                value={selectedDate ? dateKey(selectedDate) : ''}
                onChange={handleJumpChange}
                onClick={openJumpPicker}
                aria-label="Jump to date"
              />
            </span>

            <button
              type="button"
              className="tss-arrow"
              aria-label="Next 14 days"
              // Stops at the booking window rather than paging on into dates
              // the club isn't taking yet.
              disabled={isLastWindow}
              onClick={() => setWinStart(w => w + WINDOW)}
            >&rsaquo;</button>
          </div>

          <div className="tss-winlabel">{winLabel.toUpperCase()}</div>

          <div className="tss-chips">
            {Array.from({ length: winChipCount }, (_, i) => {
              const d = addDays(today, winStart + i);
              const active = sameYMD(d, selectedDate);
              const isToday = sameYMD(d, today);
              // Only Version 2 draws dates past the booking window at all.
              const locked = dateKey(d) > dateKey(lastDay);
              return (
                <button
                  type="button"
                  key={dateKey(d)}
                  className={`tss-chip${active ? ' active' : ''}${isToday ? ' today-mark' : ''}${locked ? ' locked' : ''}`}
                  disabled={locked}
                  title={locked ? BOOKING_WINDOW_LOCKED_HINT.replace('{days}', windowDays) : undefined}
                  onClick={() => onDateSelect(d)}
                >
                  <div className="tss-cd">{DOW_S[d.getDay()]}</div>
                  <div className="tss-cn">{d.getDate()}</div>
                </button>
              );
            })}
          </div>
        </div>

        {selectedDate ? (
          <div className="tss-grid">
            {/* ---- Slots column ---- */}
            <div className="tss-slots-col">
              {/* The error itself floats as a modal over the whole screen
                  (ConnectionErrorScreen portals to <body>). This cover stays
                  under it on purpose: the grid still holds the PREVIOUS
                  date's slots until the new ones land, and showing through
                  the dimmed backdrop it would read as this date's
                  availability. */}
              {fetchTimedOut && (
                <div className="tss-slots-loading">
                  <ConnectionErrorScreen onRetry={onRetryFetch} />
                </div>
              )}

              {isFetchingSlots && !fetchTimedOut && (
                <div className="tss-slots-loading" role="status" aria-live="polite">
                  {spinnerRing(`Fetching data for ${formatInPhTime(selectedDate, 'EEEE, MMMM d, yyyy')}`)}
                </div>
              )}

              <div className="tss-topinfo">
                {/* Version 2 shows the rates in the Court Rates table above
                    the card instead. */}
                {!isV2 && (
                  <div className="tss-rates-line">
                    {displayBands.map((band) => (
                      <div className="tss-rates-row" key={band.id}>
                        {band.name ? `${band.name} · ` : ''}
                        {formatBandHours(band)}
                        {': '}
                        {rateGroups
                          .map(g => `${rateGroupLabel(g)} - ${formatMoney(g.bands.find(b => b.band.id === band.id)?.price ?? 0, config)}/hr`)
                          .join(' | ')}
                      </div>
                    ))}
                  </div>
                )}
                <div className="tss-tap-line">
                  Tap an open slot below to book
                  {/* Said up front, so the rule isn't first met as a
                      disabled Reserve button. */}
                  {minBookingHours > 1 && ` \u00b7 Minimum ${minBookingHours} hours`}
                </div>
                <div className="tss-mlegend">
                  <span><span className="tss-sw tss-sw-o"></span>Open</span>
                  <span><span className="tss-sw tss-sw-b"></span>Booked</span>
                  <span><span className="tss-sw tss-sw-pr"></span>Pending</span>
                  <span><span className="tss-sw tss-sw-pa"></span>Passed</span>
                  <span><span className="tss-sw tss-sw-op"></span>Open Play</span>
                  <span><span className="tss-sw tss-sw-promo"></span>Promo</span>
                  {courts.some(c => !courtAvailability[c]) && (
                    <span><span className="tss-sw tss-sw-un"></span>Unavailable</span>
                  )}
                </div>
              </div>

              <div className="tss-slots-head">
                <h2>Available hours</h2>
                <span className="tss-hint">{hoursHint}</span>
              </div>

              {/* Horizontal scrollbar, above the grid and drawn rather than
                  native so it shows on iOS and Android too. The grid stays
                  swipeable, so this is a redundant affordance. */}
              <div className="tss-scrollbar" ref={trackRef} aria-hidden="true">
                <div className="tss-scrollbar-thumb" ref={thumbRef} />
              </div>

              <div className="tss-grid-container" ref={scrollerRef}>
                <div
                  ref={slotsGridRef}
                  className="tss-slots-grid"
                  style={{ '--tss-court-count': courts.length || 1 }}
                >
                  <span className="tss-grid-spacer" aria-hidden="true" style={{ gridRow: 1, gridColumn: 1 }}></span>
                  {/* The admin's own court name (Config -> Sports & Courts),
                      falling back to "Court {number}" when unnamed, with the
                      indoor/outdoor sub-label underneath when that's set. */}
                  {courts.map((court, courtIndex) => {
                    const envLabel = getCourtEnvLabel(config, court);
                    const closed = !courtAvailability[court];
                    // A trailing "(...)" in the court's name is a secondary
                    // detail (a local nickname, a sponsor), not part of what
                    // the court is called — set in the same bold heading type
                    // it competes with the name for attention.
                    const { name: courtName, note: courtNote } = splitCourtLabelNote(getCourtLabel(config, court));
                    return (
                      <span key={court} className={`tss-court-header${closed ? ' closed' : ''}`} style={{ gridRow: 1, gridColumn: courtIndex + 2 }}>
                        <span className="tss-court-name">
                          {courtName}
                          {courtNote && <span className="tss-court-note"> {courtNote}</span>}
                        </span>
                        {/* A closed court's sub-label says so instead of
                            indoor/outdoor — that's the more useful fact
                            while it's out of service. */}
                        {closed
                          ? <span className="tss-court-env tss-court-closed">(unavailable)</span>
                          : envLabel && <span className="tss-court-env">({envLabel})</span>}
                      </span>
                    );
                  })}

                  {gridRows.map((row, rowIndex) => {
                    const timeSlot = row.slot;
                    const rowDate = row.dateStr;
                    const isNextDay = row.dayOffset === 1;
                    const timeLabel = formatTimeSlotRangeParts(timeSlot);
                    const separator = rateSeparators.separators.get(rowIndex);
                    return (
                      // Keyed by date AND slot: the same label can appear on
                      // two dates in one grid.
                      <React.Fragment key={`${rowDate}|${timeSlot}`}>
                        {/* Heads every run of hours on one rate: a full-width
                            row with the rate's icon, name, price and hours. */}
                        {separator && (
                          <RateSeparatorRow
                            line={separator.line}
                            design={separatorDesign}
                            band={separator.band}
                            price={getRateSeparatorPriceText(config, courts, resolvedSport?.id || null, separator.band, rowDate)}
                            hours={formatBandHours(separator.band, ' \u2013 ')}
                          />
                        )}
                        {/* formatTimeSlotRange, not the raw config label —
                            renders "6:00-7:00 AM" instead of "6-7AM",
                            matching the summary column and the admin grid. */}
                        {/* Meridiem in its own span so it can be set smaller
                            — see .tss-time-meridiem. It is a unit, not part
                            of the number, and at full weight it crowds the
                            hours in this narrow sticky column. */}
                        {/* "12:00 - 1:00" on one line with each end's meridiem
                            centred underneath its own number — both ends
                            labelled on every slot, including the ones that
                            stay inside one half of the day. */}
                        {/* Every cell is placed explicitly (row + column)
                            rather than left to auto-flow: a block booking
                            spans several rows, and auto-placement would push
                            the cells after it into the wrong columns. */}
                        <span className="tss-time-cell" style={{ gridRow: rowLines[rowIndex], gridColumn: 1 }}>
                          <span className="tss-time-h">{timeLabel.start}</span>
                          <span className="tss-time-dash">{'–'}</span>
                          <span className="tss-time-h">{timeLabel.end}</span>
                          <span className="tss-time-meridiem tss-time-meridiem-start">
                            {timeLabel.startMeridiem}
                          </span>
                          <span className="tss-time-meridiem tss-time-meridiem-end">
                            {timeLabel.endMeridiem}
                          </span>
                          {/* Only on the rows that have crossed midnight —
                              putting a date on every row would be noise, and
                              its absence is what makes it read as "still the
                              day you picked". */}
                          {isNextDay && (
                            <span className="tss-time-nextday">{formatNextDayLabel(rowDate)}</span>
                          )}
                        </span>
                        {courts.map((court, courtIndex) => {
                          const spanKey = `${rowIndex}|${court}`;
                          // Swallowed by a block button drawn on an earlier row.
                          if (blockSpans.covered.has(spanKey)) return null;
                          const span = blockSpans.starts.get(spanKey);
                          if (span) {
                            return renderBlockCell(span, gridRows.slice(rowIndex, rowIndex + span.rowSpan), court, rowIndex, courtIndex);
                          }

                          const cell = resolveCellState(row, court);
                          return (
                            <button
                              key={court}
                              type="button"
                              className={`tss-slot ${cell.status}${cell.onPromo ? ' promo' : ''}`}
                              style={{ gridRow: rowLines[rowIndex], gridColumn: courtIndex + 2 }}
                              disabled={!cell.clickable}
                              onClick={() => {
                                if (!cell.clickable) return;
                                if (cell.isOpenPlay) { onJoinOpenPlay?.(rowDate, timeSlot, court); return; }
                                onCourtToggle(rowDate, timeSlot, court);
                              }}
                              title={cell.title}
                            >
                              {/* Three layouts, one per kind of cell. A promo
                                  hour gets its own because it carries three
                                  things — the flag, the status and the price —
                                  which is one more than the centred single
                                  line every other cell uses. */}
                              {cell.isOpenPlay ? (
                                <span className="tss-slot-openplay">
                                  <span className="tss-op-main">OPEN PLAY</span>
                                  <span className="tss-op-sub">(Click to join)</span>
                                </span>
                              ) : cell.onPromo ? (
                                <>
                                  {/* Ribbon and price are BOTH taken out of the
                                      flow, which leaves the status word as the
                                      cell's only flow child — so it centres
                                      exactly where it does on an ordinary open
                                      hour, and the promo trimmings hang off it
                                      instead of pushing it around. */}
                                  <span className="tss-slot-promo-label">PROMO</span>
                                  <span className="tss-slot-status">{cell.label}</span>
                                  <span className="tss-slot-promo-price">{formatMoney(cell.promoPrice, config)}</span>
                                </>
                              ) : (
                                <span className="tss-slot-status">{cell.label}</span>
                              )}
                            </button>
                          );
                        })}
                      </React.Fragment>
                    );
                  })}
                </div>
                {/* A day the club has no hours for at all — an empty set in
                    Config -> Sports & Courts -> Time Slots. Said outright,
                    because a grid with court headers and no rows under them
                    reads as a page that failed to load. */}
                {gridRows.length === 0 && (
                  <p className="tss-closed-day">
                    Closed on this day. Please pick another date.
                  </p>
                )}
              </div>
            </div>

            {/* ---- Summary column ---- */}
            <div className={`tss-court-col${floatingSummary ? ' tss-court-col-floating' : ''}`}>
              <div
                ref={summaryRef}
                className={`tss-summary${floatingSummary ? ' tss-summary-floating' : ''}`}
                role={floatingSummary ? 'region' : undefined}
                aria-label={floatingSummary ? 'Selected hours' : undefined}
              >
                {floatingSummary && (
                  <div className="tss-float-head">
                    <span className="tss-float-title">Selected hours</span>
                    <button
                      type="button"
                      className="tss-float-close"
                      onClick={() => setSummaryDocked(true)}
                      aria-label="Close — show the selected hours below the time slots"
                      title="Close"
                    >&times;</button>
                  </div>
                )}
                {/* Capped at 150px in the floating panel (CSS); the drawn
                    bar shows only when the cards overflow that. */}
                <VerticalScrollArea wrapClassName="tss-sel-area" className="tss-sel-list">
                  {totalSelectedCourts === 0 ? (
                    <span className="tss-empty-note">Tap an open hour to start.</span>
                  ) : (
                    <SelectedSlotsSummary
                      groups={summaryGroups}
                      showDates={distinctDateCount > 1}
                      formatDate={formatDateBadge}
                      onRemove={row => onCourtToggle(row.dateStr, row.timeSlot, row.court)}
                    />
                  )}
                </VerticalScrollArea>
                <div className="tss-divider"></div>
                <div className="tss-row">
                  <span className="tss-lbl">{totalSelectedCourts} hour(s)</span>
                  <span className="tss-total">{formatMoney(totalPrice, config)}</span>
                </div>
                <div className={`tss-actions${stackedLayout ? ' tss-actions-row' : ''}`}>
                  <button
                    type="button"
                    className={`tss-reserve${distinctDateCount > 1 ? ' tss-reserve-multi' : ''}`}
                    onClick={onContinue}
                    // Config -> Booking Settings: below the minimum, staggered
                    // when connecting hours are required, or half a block.
                    // The reason is printed under the buttons.
                    disabled={!canContinue || !!ruleIssue}
                    aria-describedby={ruleIssue ? 'tss-rule-msg' : undefined}
                  >
                    {/* Same "Reserve N hours" wording whether one date or
                        several — the old single-date branch said "Continue to
                        Details", which described the next screen rather than
                        what the guest is actually doing. */}
                    {totalSelectedCourts === 0
                      ? 'Select hours to continue'
                      : `Reserve ${totalSelectedCourts} hour${totalSelectedCourts === 1 ? '' : 's'} · ${formatMoney(totalPrice, config)}`}
                  </button>
                  <div className="tss-clear-wrap">
                    <button type="button" className="tss-clear" disabled={totalSelectedCourts === 0} onClick={onClearAll}>Clear</button>
                  </div>
                </div>
                {ruleIssue && (
                  <p id="tss-rule-msg" className="tss-rule-msg" role="status" aria-live="polite">
                    {ruleIssue.message}
                  </p>
                )}
              </div>
            </div>

            {/* ---- Hosted Open Play sessions ---- */}
            {/* Same card list/style as the admin grid's — clicking a card
                here shows its details plus a "coming soon" notice instead
                of an edit form (see App.jsx's handleOpenPlaySessionClick).
                Renders nothing (no wrapper element) when there are no
                upcoming sessions, so this grid area just collapses to
                nothing instead of leaving an empty bordered gap — see
                TimeSlotSelection.css's ".tss-grid > .opsc-section" rule. */}
            <OpenPlaySessionsSection sportId={sportId} variant="guest" onCardClick={onOpenPlaySessionClick} />

            {/* ---- Court Information accordion ---- */}
            {/* id target for the header's Rates/Facilities/Open Play/
                Location/FAQ nav links when the landing page is disabled
                (Config -> System) — see App.jsx's handleBackToHome /
                scrollToCourtInformation. */}
            <div className="tss-faq-col" id="court-information-section">
              <h2 className="tss-faq-col-title">Court Information</h2>
              <div className="tss-faq-list">
                {faqItems.map((item, idx) => {
                  const isOpen = openFaq === idx;
                  return (
                    <div className={`tss-faq-item${isOpen ? ' open' : ''}`} key={item.title}>
                      <button
                        type="button"
                        className="tss-faq-q"
                        aria-expanded={isOpen}
                        onClick={() => setOpenFaq(isOpen ? null : idx)}
                      >
                        <span>{item.title}</span>
                        <svg className="tss-faq-chev" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M9 18l6-6-6-6" />
                        </svg>
                      </button>
                      {isOpen && (
                        <div className="tss-faq-a">
                          <div className="tss-faq-a-inner">{item.content}</div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        ) : (
          <p className="tss-placeholder-text">Please select a date first</p>
        )}
      </div>
    </div>
  );
}

export default TimeSlotSelection;
